import { type ApolloError, useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { GET_DRAFTS_QUERY } from '../graphql/resolvers/draftQueries/getDrafts.gql';
import type { PostDraftInterface } from '../interfaces/post.interface';

export function useDrafts(userId: string | undefined): {
  drafts: PostDraftInterface[];
  postDrafts: PostDraftInterface[];
  replyDrafts: PostDraftInterface[];
  loading: boolean;
  error?: ApolloError;
  refetch: ReturnType<
    typeof useQuery<{ getDrafts: PostDraftInterface[] }>
  >['refetch'];
  getDraftById: (id: string) => PostDraftInterface | undefined;
} {
  const { data, loading, error, refetch } = useQuery(GET_DRAFTS_QUERY, {
    variables: { userId: userId ?? '' },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const drafts = useMemo<PostDraftInterface[]>(
    () => data?.getDrafts ?? [],
    [data],
  );
  const postDrafts = useMemo(
    () => drafts.filter((d) => d.parentPostId == null),
    [drafts],
  );
  const replyDrafts = useMemo(
    () => drafts.filter((d) => d.parentPostId != null),
    [drafts],
  );
  const getDraftById = useCallback(
    (id: string) => drafts.find((d) => d.id === id),
    [drafts],
  );

  return {
    drafts,
    postDrafts,
    replyDrafts,
    loading,
    error,
    refetch,
    getDraftById,
  };
}
